import { COUNTRY_STATUS_TITLES, type CountryPanelRow, type Funnel, type Partner } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { date } from '../../lib/format';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Checkbox, Field, Input, Select, Textarea } from '../../ui/form';
import { Panel } from '../../ui/layout';
import {
  countriesQuery,
  funnelQuery,
  num,
  partnerReportQuery,
  partnersQuery,
  type SpendRow,
  spendQuery,
} from './market-queries';
import { plusDays, todayIso } from './shared';

const GROUPS: Array<{ value: Funnel['groupBy']; title: string }> = [
  { value: 'country', title: 'По странам' },
  { value: 'channel', title: 'По каналам' },
  { value: 'partner', title: 'По партнёрам' },
];

/** Таблица с прокруткой вбок внутри блока: страница на телефоне не уезжает */
function Table({ head, children }: { head: string[]; children: React.ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[560px] text-left text-[14px]">
        <thead>
          <tr className="border-b border-line text-[12px] text-ink-2">
            {head.map((h, i) => (
              <th key={h} className={`px-4 py-2 font-medium ${i ? 'text-right' : ''}`}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-line">{children}</tbody>
      </table>
    </div>
  );
}
const tdNum = 'num px-4 py-2.5 text-right whitespace-nowrap';

function useRefresh() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['platform'] });
}

/* ───────────────────────── Воронка (МР-19) ───────────────────────── */

function FunnelPanel({ from, to }: { from: string; to: string }) {
  const [groupBy, setGroupBy] = useState<Funnel['groupBy']>('country');
  const q = useQuery(funnelQuery(from, to, groupBy));
  return (
    <Panel
      title="Воронка"
      meta="МР-19 · заявки → пробные → платящие"
      actions={
        <Select
          aria-label="Разрез воронки"
          className="h-11! md:h-9!"
          value={groupBy}
          onChange={(e) => setGroupBy(e.target.value as Funnel['groupBy'])}
        >
          {GROUPS.map((g) => (
            <option key={g.value} value={g.value}>
              {g.title}
            </option>
          ))}
        </Select>
      }
      bodyClassName="p-0"
    >
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorBox error={q.error} />
      ) : (
        <>
          <p className="border-b border-line px-5 py-3 text-[13px] text-ink-2">
            Входов в демо за период: <span className="num font-medium text-ink">{num(q.data.demos)}</span>
          </p>
          {q.data.rows.length ? (
            <Table head={['Группа', 'Заявки', 'Пробные', 'Платящие', 'Расходы, ₸', 'Цена заявки, ₸']}>
              {q.data.rows.map((r) => (
                <tr key={r.key}>
                  <td className="px-4 py-2.5 font-medium text-ink [overflow-wrap:anywhere]">
                    {r.key || 'не указано'}
                  </td>
                  <td className={tdNum}>{num(r.requests)}</td>
                  <td className={tdNum}>{num(r.trials)}</td>
                  <td className={tdNum}>{num(r.paying)}</td>
                  <td className={tdNum}>{num(r.spendKzt)}</td>
                  <td className={tdNum}>{num(r.costPerRequest)}</td>
                </tr>
              ))}
            </Table>
          ) : (
            <div className="p-5">
              <EmptyState title="За период ничего нет">
                Заявки и регистрации появятся здесь по мере прихода.
              </EmptyState>
            </div>
          )}
        </>
      )}
    </Panel>
  );
}

/* ───────────────────────── Панель по странам (МР-25) ───────────────────────── */

function CountryPanel() {
  const [year, setYear] = useState(todayIso().slice(0, 4));
  const q = useQuery({
    queryKey: ['platform', 'country-panel', year],
    queryFn: () => api<CountryPanelRow[]>('/platform/country-panel', { query: { year } }),
    enabled: /^\d{4}$/.test(year),
  });
  return (
    <Panel
      title="Панель по странам"
      meta="МР-25 · выручка за год и отклонение от цели"
      actions={
        <Input
          aria-label="Год"
          className="h-11! w-24 md:h-9!"
          type="number"
          inputMode="numeric"
          min={2020}
          max={2100}
          value={year}
          onChange={(e) => setYear(e.target.value)}
        />
      }
      bodyClassName="p-0"
    >
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorBox error={q.error} />
      ) : q.data.length ? (
        <Table head={['Страна', 'Заявки', 'Пробные', 'Платящие', 'Ушли', 'Выручка, ₸', 'Цель', 'Отклонение']}>
          {q.data.map((r) => (
            <tr key={r.code}>
              <td className="px-4 py-2.5">
                <span className="font-medium text-ink">{r.name}</span>{' '}
                <span className="text-[12px] text-ink-2">{COUNTRY_STATUS_TITLES[r.status]}</span>
              </td>
              <td className={tdNum}>{num(r.requests)}</td>
              <td className={tdNum}>{num(r.trials)}</td>
              <td className={tdNum}>{num(r.paying)}</td>
              <td className={tdNum}>{num(r.churned)}</td>
              <td className={tdNum} title={`В валюте страны: ${num(r.revenueLocal)} ${r.currency}`}>
                {r.revenueKzt == null ? 'нет курса' : num(r.revenueKzt)}
              </td>
              <td className={tdNum}>{num(r.targetRevenue)}</td>
              <td className={`${tdNum} ${r.deviation != null && r.deviation < 0 ? 'text-danger' : ''}`}>
                {r.deviation == null ? '—' : `${r.deviation > 0 ? '+' : ''}${num(r.deviation)}`}
              </td>
            </tr>
          ))}
        </Table>
      ) : (
        <div className="p-5">
          <EmptyState title="Стран нет">Добавьте страны в разделе «Страны и рынки».</EmptyState>
        </div>
      )}
    </Panel>
  );
}

/* ───────────────────────── Партнёры (МР-16) ───────────────────────── */

function PartnerDialog({ partner, onClose }: { partner: Partner | 'new'; onClose: () => void }) {
  const isNew = partner === 'new';
  const refresh = useRefresh();
  const countries = useQuery(countriesQuery);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [f, setF] = useState({
    code: isNew ? '' : partner.code,
    name: isNew ? '' : partner.name,
    countryCode: isNew ? '' : (partner.countryCode ?? ''),
    contact: isNew ? '' : (partner.contact ?? ''),
    commissionPct: isNew ? '0' : String(partner.commissionPct),
    notes: isNew ? '' : (partner.notes ?? ''),
    isActive: isNew ? true : partner.isActive,
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF((x) => ({ ...x, [k]: e.target.value }));
  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: f.name,
        countryCode: f.countryCode || null,
        contact: f.contact.trim() || null,
        commissionPct: Number(f.commissionPct),
        notes: f.notes.trim() || null,
      };
      return isNew
        ? api('/platform/partners', { body: { ...body, code: f.code } })
        : api(`/platform/partners/${partner.id}`, {
            method: 'PATCH',
            body: { ...body, isActive: f.isActive },
          });
    },
    onSuccess: () => {
      refresh();
      toast(isNew ? 'Партнёр добавлен' : 'Партнёр сохранён');
      onClose();
    },
    onError: (err) => err instanceof ApiError && setErrors(err.fieldErrors()),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setErrors({});
    save.mutate();
  };
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={isNew ? 'Новый партнёр' : `Партнёр «${partner.name}»`}
      description={isNew ? 'Код партнёра ставится в ссылку: адрес главной страницы с ?ref=код.' : undefined}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" type="submit" form="partner-form" loading={save.isPending}>
            {isNew ? 'Добавить' : 'Сохранить'}
          </Button>
        </>
      }
    >
      <form id="partner-form" onSubmit={submit} className="grid gap-4 sm:grid-cols-2" noValidate>
        <Field label="Название" htmlFor="pt-name" error={errors.name}>
          <Input id="pt-name" value={f.name} onChange={set('name')} invalid={!!errors.name} />
        </Field>
        <Field label="Код" htmlFor="pt-code" hint="Латиница, цифры, дефис" error={errors.code}>
          <Input
            id="pt-code"
            value={f.code}
            disabled={!isNew}
            onChange={(e) => setF((x) => ({ ...x, code: e.target.value.toLowerCase() }))}
            invalid={!!errors.code}
          />
        </Field>
        <Field label="Страна" htmlFor="pt-country" error={errors.countryCode}>
          <Select id="pt-country" value={f.countryCode} onChange={set('countryCode')}>
            <option value="">не задана</option>
            {(countries.data ?? []).map((c) => (
              <option key={c.code} value={c.code}>
                {c.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Комиссия, %" htmlFor="pt-pct" error={errors.commissionPct}>
          <Input
            id="pt-pct"
            type="number"
            inputMode="decimal"
            min={0}
            max={100}
            step="0.01"
            value={f.commissionPct}
            onChange={set('commissionPct')}
          />
        </Field>
        <Field label="Контакт" htmlFor="pt-contact" className="sm:col-span-2" error={errors.contact}>
          <Input id="pt-contact" value={f.contact} onChange={set('contact')} />
        </Field>
        <Field label="Заметки" htmlFor="pt-notes" className="sm:col-span-2" error={errors.notes}>
          <Textarea id="pt-notes" rows={2} value={f.notes} onChange={set('notes')} />
        </Field>
        {isNew ? null : (
          <Checkbox
            className="min-h-11 items-center sm:col-span-2"
            label="Действует"
            description="Выключенный партнёр остаётся в отчётах, новые компании за ним не закрепляются"
            checked={f.isActive}
            onChange={(e) => setF((x) => ({ ...x, isActive: e.target.checked }))}
          />
        )}
        <ErrorBox className="sm:col-span-2" error={Object.keys(errors).length ? null : save.error} />
      </form>
    </Dialog>
  );
}

function PartnersPanel({ from, to }: { from: string; to: string }) {
  const q = useQuery(partnersQuery);
  const report = useQuery(partnerReportQuery(from, to));
  const [editing, setEditing] = useState<Partner | 'new' | null>(null);
  return (
    <Panel
      title="Партнёры"
      meta="МР-16"
      actions={
        <Button
          variant="primary"
          size="sm"
          icon={<Plus className="size-4" />}
          onClick={() => setEditing('new')}
        >
          Новый партнёр
        </Button>
      }
      bodyClassName="p-0"
    >
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorBox error={q.error} />
      ) : q.data.length ? (
        <ul className="divide-y divide-line">
          {q.data.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => setEditing(p)}
                className="flex min-h-[44px] w-full flex-wrap items-center justify-between gap-x-4 gap-y-1 px-5 py-3 text-left hover:bg-surface-2"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-ink">{p.name}</span>
                    <span className="text-[12px] text-ink-2">{p.code}</span>
                    {!p.isActive ? <Badge>не действует</Badge> : null}
                  </div>
                  <div className="text-[13px] text-ink-2">
                    {p.countryCode ?? 'любая страна'} · комиссия {num(p.commissionPct, 2)} %
                    {p.contact ? ` · ${p.contact}` : ''}
                  </div>
                </div>
                <span className="text-[13px] text-ink-2">компаний: {p.companies}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <div className="p-5">
          <EmptyState title="Партнёров нет">
            Партнёр получает ссылку с кодом; компании по ней закрепляются за ним.
          </EmptyState>
        </div>
      )}
      <div className="border-t border-line">
        <h3 className="px-5 pt-4 text-[14px] font-semibold text-ink">
          Отчёт по комиссии за период {date(from)} — {date(to)}
        </h3>
        {report.isPending ? (
          <Spinner />
        ) : report.isError ? (
          <ErrorBox error={report.error} />
        ) : report.data.length ? (
          <Table head={['Партнёр', 'Компаний', 'Оплачено, ₸', 'Комиссия, ₸']}>
            {report.data.map((r) => (
              <tr key={r.partnerId}>
                <td className="px-4 py-2.5 font-medium text-ink">{r.name}</td>
                <td className={tdNum}>{num(r.companies)}</td>
                <td className={tdNum}>{r.paidTotalKzt == null ? 'нет курса' : num(r.paidTotalKzt)}</td>
                <td className={tdNum}>{r.commissionKzt == null ? 'нет курса' : num(r.commissionKzt)}</td>
              </tr>
            ))}
          </Table>
        ) : (
          <p className="px-5 py-4 text-[13px] text-ink-2">За период оплат от компаний партнёров не было.</p>
        )}
      </div>
      {editing ? <PartnerDialog partner={editing} onClose={() => setEditing(null)} /> : null}
    </Panel>
  );
}

/* ───────────────────────── Расходы на маркетинг (МР-19) ───────────────────────── */

function SpendPanel() {
  const q = useQuery(spendQuery);
  const countries = useQuery(countriesQuery);
  const refresh = useRefresh();
  const [f, setF] = useState({
    month: todayIso().slice(0, 7),
    channel: '',
    campaign: '',
    countryCode: '',
    amount: '',
    currency: 'KZT',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF((x) => ({ ...x, [k]: e.target.value }));
  const add = useMutation({
    mutationFn: () =>
      api('/platform/spend', {
        body: {
          month: `${f.month}-01`,
          channel: f.channel,
          campaign: f.campaign.trim() || null,
          countryCode: f.countryCode || null,
          amount: Number(f.amount),
          currency: f.currency,
        },
      }),
    onSuccess: () => {
      refresh();
      toast('Расход записан');
      setF((x) => ({ ...x, amount: '', campaign: '' }));
    },
    onError: (err) => err instanceof ApiError && setErrors(err.fieldErrors()),
  });
  const del = useMutation({
    mutationFn: (id: string) => api(`/platform/spend/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      refresh();
      toast('Расход удалён');
    },
    onError: (err) => toast(err instanceof Error ? err.message : 'Не удалось удалить', 'danger'),
  });
  return (
    <Panel title="Расходы на маркетинг" meta="МР-19 · по ним считается цена заявки" bodyClassName="p-0">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErrors({});
          add.mutate();
        }}
        className="grid gap-3 border-b border-line p-5 sm:grid-cols-2 lg:grid-cols-6 lg:items-end"
        noValidate
      >
        <Field label="Месяц" htmlFor="sp-month" error={errors.month}>
          <Input id="sp-month" type="month" value={f.month} onChange={set('month')} />
        </Field>
        <Field label="Канал" htmlFor="sp-channel" hint="Как в utm_source" error={errors.channel}>
          <Input id="sp-channel" value={f.channel} onChange={set('channel')} />
        </Field>
        <Field label="Кампания" htmlFor="sp-camp" error={errors.campaign}>
          <Input id="sp-camp" value={f.campaign} onChange={set('campaign')} />
        </Field>
        <Field label="Страна" htmlFor="sp-country" error={errors.countryCode}>
          <Select id="sp-country" value={f.countryCode} onChange={set('countryCode')}>
            <option value="">все</option>
            {(countries.data ?? []).map((c) => (
              <option key={c.code} value={c.code}>
                {c.code}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Сумма" htmlFor="sp-amount" error={errors.amount}>
          <Input
            id="sp-amount"
            type="number"
            inputMode="decimal"
            min={0}
            value={f.amount}
            onChange={set('amount')}
          />
        </Field>
        <Field label="Валюта" htmlFor="sp-cur" error={errors.currency}>
          <Input
            id="sp-cur"
            maxLength={3}
            value={f.currency}
            onChange={(e) => setF((x) => ({ ...x, currency: e.target.value.toUpperCase() }))}
          />
        </Field>
        <div className="sm:col-span-2 lg:col-span-6">
          <Button
            variant="primary"
            type="submit"
            loading={add.isPending}
            disabled={!f.channel.trim() || !f.amount}
          >
            Записать расход
          </Button>
        </div>
        <ErrorBox
          className="sm:col-span-2 lg:col-span-6"
          error={Object.keys(errors).length ? null : add.error}
        />
      </form>
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorBox error={q.error} />
      ) : q.data.length ? (
        <ul className="divide-y divide-line">
          {q.data.map((s: SpendRow) => (
            <li
              key={s.id}
              className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-5 py-2.5 text-[14px]"
            >
              <div className="min-w-0 text-ink [overflow-wrap:anywhere]">
                <span className="font-medium">{s.month.slice(0, 7)}</span> · {s.channel}
                {s.campaign ? ` · ${s.campaign}` : ''}
                {s.countryCode ? ` · ${s.countryCode}` : ''}
              </div>
              <div className="flex items-center gap-2">
                <span className="num text-ink-2">
                  {num(s.amount)} {s.currency}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Удалить расход ${s.channel} за ${s.month.slice(0, 7)}`}
                  icon={<Trash2 className="size-4" />}
                  onClick={() => del.mutate(s.id)}
                />
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className="p-5">
          <EmptyState title="Расходов нет">Без расходов цена заявки не считается.</EmptyState>
        </div>
      )}
    </Panel>
  );
}

/** Продажи (ТЗ 6.30): воронка, панель по странам, партнёры с отчётом, расходы на маркетинг */
export function SalesPage() {
  const [to, setTo] = useState(todayIso());
  const [from, setFrom] = useState(plusDays(todayIso(), -90));
  const valid = /^\d{4}-\d{2}-\d{2}$/.test(from) && /^\d{4}-\d{2}-\d{2}$/.test(to) && from <= to;
  return (
    <div className="flex flex-col gap-6">
      <div className="grid max-w-md grid-cols-2 gap-3">
        <Field label="Период с" htmlFor="sl-from">
          <Input
            id="sl-from"
            type="date"
            className="h-11!"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </Field>
        <Field label="по" htmlFor="sl-to">
          <Input
            id="sl-to"
            type="date"
            className="h-11!"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </Field>
      </div>
      {valid ? (
        <>
          <FunnelPanel from={from} to={to} />
          <CountryPanel />
          <PartnersPanel from={from} to={to} />
        </>
      ) : (
        <ErrorBox error={new Error('Проверьте период: начало не позже конца')} />
      )}
      <SpendPanel />
    </div>
  );
}
